import React, { useEffect } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import classNames from 'classnames';

import { ModalV2Props } from './modal-v2.types';
import { useDragToClose } from './use-drag-to-close';

/**
 * Modal component using Headless UI v2.1+ API with smooth transitions
 *
 * Features:
 * - Smooth enter/exit animations matching Modal v1
 * - Drag to close on mobile devices
 * - Multiple size variants (sm, md, lg, xl, full)
 * - Support for multiple simultaneous modals with proper z-index stacking
 * - Fully accessible with ARIA attributes
 * - Modern Headless UI v2.1+ Transition API
 *
 * Animations:
 * - Backdrop: fade in/out (opacity-0 → opacity-100)
 * - Content: slide up from bottom (translate-y-full → translate-y-0)
 * - Timing: ease-out duration-300 (enter), ease-in duration-300 (leave)
 *
 * @example
 * ```tsx
 * <ModalV2
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   variant="md"
 *   hasMobileHandle
 * >
 *   <div className="p-6">
 *     <h2>Modal Title</h2>
 *     <p>Modal content</p>
 *   </div>
 * </ModalV2>
 * ```
 */
export const ModalV2 = ({
  isOpen,
  onClose,
  children,
  variant = 'lg',
  hasMobileHandle = false,
  enableDragToClose = true,
  maxWidthClass,
  borderRadiusClass = 'rounded-t-lg sm:rounded-lg',
  overlayClassName,
  contentClassName,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  enableMultipleModals = false,
  zIndex = 40,
  ariaLabel = 'modal',
}: ModalV2Props) => {
  // Drag to close hook
  const { currentTranslateY, isDragging, isClosing, handleTouchStart, handleTouchMove, handleTouchEnd, initiateClose } =
    useDragToClose(isOpen, onClose, enableDragToClose);

  // Handle Escape key press when closeOnEscape is disabled
  useEffect(() => {
    if (!closeOnEscape && isOpen) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      // Capture phase to intercept before Headless UI's listener
      document.addEventListener('keydown', handleKeyDown, true);
      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }
  }, [closeOnEscape, isOpen]);

  return (
    <Transition show={isOpen && !isClosing} appear>
      <Dialog
        onClose={closeOnBackdropClick ? initiateClose : () => {}}
        className='relative'
        aria-label={ariaLabel}
        __demoMode={enableMultipleModals}
        style={{ zIndex }}>
        {/* Backdrop with blur effect and transition */}
        <TransitionChild
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-300'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div
            className={classNames(
              'fixed inset-0 backdrop-blur-xs',
              'dark:bg-transparent-12 bg-transparent-30',
              overlayClassName
            )}
            aria-hidden='true'
            style={{ zIndex }}
          />
        </TransitionChild>

        {/* Content wrapper - positioned at bottom on mobile, center on desktop */}
        <TransitionChild
          enter='ease-out duration-300'
          enterFrom='translate-y-full'
          enterTo='translate-y-0'
          leave='ease-in duration-300'
          leaveFrom='translate-y-0'
          leaveTo='translate-y-full'>
          <div className='fixed inset-0 flex items-end w-screen sm:items-center sm:justify-center' style={{ zIndex }}>
            <DialogPanel
              className={classNames(
                // Base styles
                'bg-primary relative w-full sm:w-xl',
                'h-auto max-h-[95dvh] overflow-auto scrollbar scrollbar-hide',
                'sm:align-middle lg:my-8',
                'shadow-[2px_2px_20px_0_rgba(0,0,0,0.15)]',
                borderRadiusClass,

                // Size variants
                {
                  'lg:max-w-211.5': variant === 'lg' && !maxWidthClass,
                  'lg:max-w-(--breakpoint-md)': variant === 'md' && !maxWidthClass,
                  'lg:max-w-(--breakpoint-sm)': variant === 'sm' && !maxWidthClass,
                  'lg:max-w-screen-xl': variant === 'xl' && !maxWidthClass,
                  'lg:max-w-full lg:max-h-full lg:h-full lg:w-full lg:rounded-none':
                    variant === 'full' && !maxWidthClass,
                  [`${maxWidthClass}`]: !!maxWidthClass,
                },
                contentClassName
              )}
              style={{
                // Override transform when dragging (takes precedence over transition)
                transform: isDragging ? `translateY(${currentTranslateY}px)` : undefined,
                transition: isDragging ? 'none' : 'transform 0.3s ease-in-out',
              }}>
              {/* Mobile drag handle */}
              {hasMobileHandle && (
                <button
                  className='w-10 h-1 bg-transparent-20 rounded-full mx-auto mt-4 cursor-pointer block sm:hidden'
                  onClick={initiateClose}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  aria-label='Drag to close'
                />
              )}

              {/* Modal content */}
              {children}
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};
